import type { CSSProperties, ReactNode } from 'react';

/**
 * Enkel Markdown för Q:s svar (från Q works components/Markdown.tsx): rubriker, fet och kursiv text,
 * kod, länkar, listor, tabeller och stycken. Bygger React-element och aldrig rå HTML, så modellens
 * text kan inte föra in markup. Länkar visas bara för http(s) och öppnas i en ny flik.
 */
function inline(text: string, keyPrefix: string, linkColor: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyPrefix}-${i++}`;
    if (tok.startsWith('[')) {
      const [, label, href] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok)!;
      if (/^https?:\/\//.test(href)) out.push(<a key={key} href={href} target="_blank" rel="noreferrer" style={{ color: linkColor, fontWeight: 600 }}>{label}</a>);
      else out.push(label);
    } else if (tok.startsWith('**')) out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={key} style={{ fontSize: '0.88em', padding: '1px 4px', borderRadius: 4, background: 'rgba(127,127,127,0.16)' }}>{tok.slice(1, -1)}</code>);
    else out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const cells = (row: string) => row.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());

export function QMarkdown({ text, linkColor, borderColor }: { text: string; linkColor: string; borderColor: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  const cell: CSSProperties = { borderBottom: `1px solid ${borderColor}`, padding: '5px 8px', textAlign: 'left', verticalAlign: 'top' };
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push(<p key={k++} style={{ margin: 0, fontWeight: 700, fontSize: heading[1].length <= 2 ? 17 : 15 }}>{inline(heading[2], `h${k}`, linkColor)}</p>);
      i++;
      continue;
    }
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) rows.push(cells(lines[i++]));
      blocks.push(
        <div key={k++} style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr>{head.map((h, j) => <th key={j} style={{ ...cell, fontWeight: 700 }}>{inline(h, `th${k}-${j}`, linkColor)}</th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, j) => <td key={j} style={cell}>{inline(c, `td${k}-${ri}-${j}`, linkColor)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }
    const listRe = /^\s*([-*•]|\d+[.)])\s+(.*)$/;
    if (listRe.test(line)) {
      const ordered = /^\s*\d/.test(line);
      const items: string[] = [];
      while (i < lines.length && listRe.test(lines[i])) items.push(listRe.exec(lines[i++])![2]);
      const ListTag = ordered ? 'ol' : 'ul';
      blocks.push(<ListTag key={k++} style={{ margin: 0, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 3 }}>{items.map((it, j) => <li key={j}>{inline(it, `li${k}-${j}`, linkColor)}</li>)}</ListTag>);
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !listRe.test(lines[i]) && !/^#{1,4}\s/.test(lines[i])) para.push(lines[i++]);
    blocks.push(<p key={k++} style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{inline(para.join('\n'), `p${k}`, linkColor)}</p>);
  }
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10, lineHeight: 1.5 }}>{blocks}</div>;
}
